// pages/Notifications.jsx
import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Chip,
  Button,
} from "@mui/material";
import formatDate from "../components/FormatDate";
import { API_BASE_URL } from "../config";

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const token = localStorage.getItem("token");

  const [updatingIds, setUpdatingIds] = useState({});
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = async (requestedPage = 0, rpp = rowsPerPage) => {
    try {
      const offsetCalc = requestedPage * rpp;
      const res = await fetch(
        `${API_BASE_URL}/api/user/notifications?limit=${rpp}&offset=${offsetCalc}&scope=all`,
        {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        }
      );
      const raw = await res.json();
      const totalHeader = res.headers.get("x-total-count");
      const totalNum = totalHeader ? parseInt(totalHeader, 10) : 0;
      const data = Array.isArray(raw)
        ? raw.map((n) => ({
            ...n,
            is_read:
              n.is_read === true ||
              n.is_read === 1 ||
              n.is_read === "1" ||
              n.is_read === "true",
          }))
        : [];

      setNotifications(data);
      setTotal(Number.isFinite(totalNum) ? totalNum : data.length);
      setHasMore(
        offsetCalc + data.length <
          (Number.isFinite(totalNum) ? totalNum : offsetCalc + data.length)
      );
      setPage(requestedPage);
    } catch (err) {
      console.error("Failed to load notifications", err);
    }
  };

  const markAsRead = async (notifId) => {
    // optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === notifId ? { ...n, is_read: true } : n))
    );
    setUpdatingIds((prev) => ({ ...prev, [notifId]: true }));

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/user/notifications/${notifId}/read`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        }
      );
      if (!res.ok) {
        // rollback on failure
        setNotifications((prev) =>
          prev.map((n) => (n.id === notifId ? { ...n, is_read: false } : n))
        );
      }
    } catch (err) {
      // rollback on error
      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, is_read: false } : n))
      );
      console.error("Failed to mark as read", err);
    } finally {
      setUpdatingIds((prev) => {
        const copy = { ...prev };
        delete copy[notifId];
        return copy;
      });
    }
  };

  const markAllAsRead = async () => {
    try {
      setMarkingAll(true);
      const res = await fetch(
        `${API_BASE_URL}/api/user/notifications/mark-all-read`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        }
      );
      if (res.ok) {
        // optimistic UI
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        await fetchNotifications(page, rowsPerPage);
      } else {
        console.warn("Mark-all endpoint responded with", res.status);
      }
    } catch (err) {
      console.error("Failed to mark all as read", err);
    } finally {
      setMarkingAll(false);
    }
  };

  useEffect(() => {
    fetchNotifications(page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight={500}>
          All Notifications
        </Typography>
        <Button
          variant="outlined"
          onClick={markAllAsRead}
          disabled={markingAll}
        >
          {markingAll ? "Marking..." : "Mark all as read"}
        </Button>
      </Box>

      <Paper sx={{ minHeight: 400 }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Message</TableCell>
                <TableCell width={200}>Date</TableCell>
                <TableCell width={120}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notifications.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    align="center"
                    sx={{ py: 6, color: "text.secondary" }}
                  >
                    No notifications found.
                  </TableCell>
                </TableRow>
              ) : (
                notifications.map((n) => (
                  <TableRow
                    hover
                    key={`${n.id}-${n.created_at}`}
                    onClick={() => {
                      if (!n.is_read && !updatingIds[n.id]) markAsRead(n.id);
                    }}
                    sx={{
                      cursor: "pointer",
                      bgcolor: !n.is_read ? "#e3f2fd" : "inherit",
                    }}
                  >
                    <TableCell
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {n.message}
                    </TableCell>
                    <TableCell>{formatDate(n.created_at)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={n.is_read ? "Read" : "Unread"}
                        color={n.is_read ? "default" : "primary"}
                        variant={n.is_read ? "outlined" : "filled"}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!n.is_read && !updatingIds[n.id])
                            markAsRead(n.id);
                        }}
                        disabled={!!updatingIds[n.id]}
                        sx={{ cursor: n.is_read ? "default" : "pointer" }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 25]}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            const next = parseInt(e.target.value, 10);
            setRowsPerPage(next);
            setPage(0);
          }}
          count={total}
        />
      </Paper>
    </Box>
  );
};

export default Notifications;
